import { useState, useEffect, useCallback } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Shield, AlertCircle, Loader2, Save, Eye, EyeOff, ExternalLink, CheckCircle, XCircle, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { 
  stripeConfigurationService,
  type StripeConfiguration as StripeConfigurationData,
  type StripeConfigurationRequest,
  type StripeConfigurationUpdateRequest
} from '@/services/billing/stripe-configuration.service';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const stripeConfigurationSchema = z.object({
  stripe_publishable_key: z.string()
    .refine(
      (val) => !val || /^pk_(test_|live_)/.test(val),
      'Invalid publishable key format'
    )
    .optional(),
  stripe_secret_key: z.string()
    .refine(
      (val) => !val || /^sk_(test_|live_)/.test(val),
      'Invalid secret key format'
    )
    .optional(),
  stripe_webhook_secret: z.string().optional(),
  stripe_account_country: z.string().min(1, 'Country is required'),
  stripe_account_currency: z.string().min(1, 'Currency is required'),
  stripe_live_mode: z.boolean(),
  stripe_business_type: z.string().optional(),
});

type StripeConfigurationFormData = z.infer<typeof stripeConfigurationSchema>;

interface StripeConfigurationProps {
  className?: string;
}

export function StripeConfiguration({ className }: StripeConfigurationProps) {
  const { t } = useTranslation();
  // State management
  const [config, setConfig] = useState<StripeConfigurationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  // Form setup
  const form = useForm<StripeConfigurationFormData>({
    resolver: zodResolver(stripeConfigurationSchema),
    defaultValues: {
      stripe_publishable_key: '',
      stripe_secret_key: '',
      stripe_webhook_secret: '',
      stripe_account_country: 'US',
      stripe_account_currency: 'USD',
      stripe_live_mode: false,
      stripe_business_type: 'individual',
    },
  });

  const loadConfiguration = useCallback(async () => {
    try {
      setIsLoading(true);
      const configuration = await stripeConfigurationService.getStripeConfiguration();
      setConfig(configuration);
      
      if (configuration.configured) {
        // For existing configurations, populate non-sensitive fields
        form.reset({
          stripe_publishable_key: configuration.publishable_key_preview || '',
          stripe_secret_key: '', // Keep secret fields empty for security
          stripe_webhook_secret: '',
          stripe_live_mode: configuration.live_mode,
          stripe_account_country: configuration.account_country || 'US',
          stripe_account_currency: configuration.account_currency || 'USD',
          stripe_business_type: configuration.business_type || 'individual',
        });
      } else {
        // For new configurations, use default values
        form.reset({
          stripe_publishable_key: '',
          stripe_secret_key: '',
          stripe_webhook_secret: '',
          stripe_live_mode: false,
          stripe_account_country: 'US',
          stripe_account_currency: 'USD',
          stripe_business_type: 'individual',
        });
      }
    } catch (_error) {
      console.error('Failed to load Stripe configuration:', _error);
      setConfig(null);
    } finally {
      setIsLoading(false);
    }
  }, [form]);

  // Load configuration on mount
  useEffect(() => {
    void loadConfiguration();
  }, [loadConfiguration]);

  const validateCredentials = async () => {
    const values = form.getValues();
    if (!values.stripe_publishable_key || !values.stripe_secret_key) {
      toast.error('Please enter both publishable and secret keys');
      return false;
    }

    try {
      setIsValidating(true);
      const result = await stripeConfigurationService.validateStripeCredentials({
        stripe_publishable_key: values.stripe_publishable_key,
        stripe_secret_key: values.stripe_secret_key
      });

      if (result.valid) {
        toast.success('Stripe credentials are valid');
        return true;
      } else {
        toast.error(result.message || 'Invalid Stripe credentials');
        return false;
      }
    } catch (_error) {
      toast.error('Failed to validate credentials');
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const testConnection = async () => {
    try {
      setIsTesting(true);
      console.log('🔍 Starting test connection...');
      const result = await stripeConfigurationService.testStripeConnection();
      
      console.log('🔍 Test connection result:', result);
      
      if (result.success) {
        console.log('✅ Showing success toast');
        toast.success('Stripe connection test successful');
      } else {
        console.log('❌ Showing error toast:', result.message);
        toast.error(result.message || 'Connection test failed');
      }
    } catch (_error) {
      console.error('💥 Test connection error:', _error);
      toast.error('Failed to test connection');
    } finally {
      setIsTesting(false);
    }
  };

  const refreshCapabilities = async () => {
    try {
      setIsRefreshing(true);
      const updatedConfig = await stripeConfigurationService.refreshStripeCapabilities();
      setConfig(updatedConfig);
      toast.success('Stripe capabilities refreshed');
    } catch (_error) {
      toast.error('Failed to refresh capabilities');
    } finally {
      setIsRefreshing(false);
    }
  };

  const onSubmit = async (data: StripeConfigurationFormData) => {
    try {
      setIsSaving(true);

      // Validate required fields for new configurations
      if (!config?.configured) {
        if (!data.stripe_publishable_key) {
          toast.error('Publishable key is required for new configurations');
          setIsSaving(false);
          return;
        }
        if (!data.stripe_secret_key) {
          toast.error('Secret key is required for new configurations');
          setIsSaving(false);
          return;
        }
      }

      // For updates, only send fields that have values or are required
      const requestData = { ...data };
      
      // If updating existing config and sensitive fields are empty, don't send them
      if (config?.configured) {
        if (!data.stripe_publishable_key || data.stripe_publishable_key === config.publishable_key_preview) {
          delete requestData.stripe_publishable_key;
        }
        if (!data.stripe_secret_key) {
          delete requestData.stripe_secret_key;
        }
        if (!data.stripe_webhook_secret) {
          delete requestData.stripe_webhook_secret;
        }
      }

      let updatedConfig: StripeConfigurationData;
      
      if (config?.configured) {
        updatedConfig = await stripeConfigurationService.updateStripeConfiguration(requestData as StripeConfigurationUpdateRequest);
        toast.success('Stripe configuration updated successfully');
      } else {
        updatedConfig = await stripeConfigurationService.createStripeConfiguration(data as StripeConfigurationRequest);
        toast.success('Stripe configuration created successfully');
      }

      setConfig(updatedConfig);
      
      // Reset form with updated configuration but clear sensitive fields
      form.reset({
        stripe_publishable_key: updatedConfig.publishable_key_preview || '',
        stripe_secret_key: '',
        stripe_webhook_secret: '',
        stripe_live_mode: updatedConfig.live_mode,
        stripe_account_country: updatedConfig.account_country || 'US',
        stripe_account_currency: updatedConfig.account_currency || 'USD',
        stripe_business_type: updatedConfig.business_type || 'individual',
      });
      
    } catch (_error) {
      toast.error('Failed to save Stripe configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!confirm('Are you sure you want to remove Stripe configuration? This will disable payment processing.')) {
      return;
    }

    try {
      await stripeConfigurationService.removeStripeConfiguration();
      setConfig(null);
      form.reset({
        stripe_publishable_key: '',
        stripe_secret_key: '',
        stripe_webhook_secret: '',
        stripe_account_country: 'US',
        stripe_account_currency: 'USD',
        stripe_live_mode: false,
        stripe_business_type: 'individual'
      });
      toast.success('Stripe configuration removed');
    } catch (_error) {
      toast.error('Failed to remove Stripe configuration');
    }
  };

  const getCapabilityBadgeVariant = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'pending': return 'secondary';
      case 'inactive': return 'destructive';
      default: return 'secondary';
    }
  };

  const getCapabilityIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-3 w-3" />;
      case 'pending': return <Loader2 className="h-3 w-3 animate-spin" />;
      case 'inactive': return <XCircle className="h-3 w-3" />;
      default: return <AlertCircle className="h-3 w-3" />;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <Skeleton className="h-6 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t('products.stripe.title')}
            </CardTitle>
            <CardDescription>
              {t('products.stripe.description')}
            </CardDescription>
          </div>
          {config?.configured && (
            <div className="flex items-center gap-2">
              <Badge variant={config.live_mode ? "default" : "secondary"}>
                {config.live_mode ? t('products.stripe.badgeLive') : t('products.stripe.badgeTest')}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={testConnection}
                disabled={isTesting}
              >
                {isTesting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t('products.stripe.testConnection')}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Current Status */}
        {config?.configured && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <span>
                  {t('products.stripe.statusConfigured', {
                    mode: t(config.live_mode ? 'products.stripe.mode.live' : 'products.stripe.mode.test'),
                  })}
                  {config.configured_at && (
                    <span className="text-muted-foreground ml-2">
                      {t('products.stripe.configuredOn', {
                        date: new Date(config.configured_at).toLocaleDateString(),
                      })}
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={refreshCapabilities}
                    disabled={isRefreshing}
                  >
                    <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </Button>
                  <a
                    href="https://dashboard.stripe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    {t('products.stripe.viewDashboard')}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Account Capabilities */}
        {config?.capabilities && (
          <div className="space-y-3">
            <Label className="text-base">{t('products.stripe.accountCapabilities')}</Label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(config.capabilities).map(([capability, status]) => (
                <Badge
                  key={capability}
                  variant={getCapabilityBadgeVariant(status)}
                  className="flex items-center gap-1"
                >
                  {getCapabilityIcon(status)}
                  {capability.replace('_', ' ')}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Outstanding Requirements */}
        {config?.requirements?.currently_due && config.requirements.currently_due.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <strong>{t('products.stripe.actionRequired')}</strong>
                <ul className="list-disc pl-4 space-y-1">
                  {config.requirements.currently_due.map((requirement, index) => (
                    <li key={index} className="text-sm">
                      {requirement.replace('_', ' ')}
                    </li>
                  ))}
                </ul>
                <p className="text-sm">
                  {t('products.stripe.completeRequirements')}{' '}
                  <a
                    href="https://dashboard.stripe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {t('products.stripe.dashboardLink')}
                  </a>
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <Separator />

        {/* Configuration Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Label className="text-base">{t('products.stripe.apiKeysSection')}</Label>
            
            {/* Mode Selection */}
            <FormField
              control={form.control}
              name="stripe_live_mode"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>{t('products.stripe.environmentLabel')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value.toString()}
                      onValueChange={(value) => field.onChange(value === 'true')}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="false" id="test-mode" />
                        <Label htmlFor="test-mode" className="cursor-pointer">{t('products.stripe.testMode')}</Label>
                        <Badge variant="secondary">{t('products.stripe.recommendedBadge')}</Badge>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="true" id="live-mode" />
                        <Label htmlFor="live-mode" className="cursor-pointer">{t('products.stripe.liveMode')}</Label>
                        <Badge variant="outline">{t('products.stripe.productionBadge')}</Badge>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormDescription>
                    {t('products.stripe.environmentDescription')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Publishable Key */}
            <FormField
              control={form.control}
              name="stripe_publishable_key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('products.stripe.publishableKeyLabel')}
                    {!config?.configured && <span className="text-red-500 ml-1">*</span>}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder={`pk_${form.watch('stripe_live_mode') ? 'live' : 'test'}_...`}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {config?.configured 
                      ? t('products.stripe.publishableKeyDescriptionConfigured')
                      : t('products.stripe.publishableKeyDescription')
                    }
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Secret Key */}
            <FormField
              control={form.control}
              name="stripe_secret_key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('products.stripe.secretKeyLabel')}
                    {!config?.configured && <span className="text-red-500 ml-1">*</span>}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showSecretKey ? "text" : "password"}
                        placeholder={`sk_${form.watch('stripe_live_mode') ? 'live' : 'test'}_...`}
                        className="pr-10"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowSecretKey(!showSecretKey)}
                      >
                        {showSecretKey ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormDescription>
                    {config?.configured 
                      ? t('products.stripe.secretKeyDescriptionConfigured')
                      : t('products.stripe.secretKeyDescription')
                    }
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Webhook Secret (Optional) */}
            <FormField
              control={form.control}
              name="stripe_webhook_secret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('products.stripe.webhookSecretLabel')}{' '}
                    <span className="text-muted-foreground ml-1">{t('products.stripe.optionalLabel')}</span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showWebhookSecret ? "text" : "password"}
                        placeholder="whsec_..."
                        className="pr-10"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                      >
                        {showWebhookSecret ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormDescription>
                    {t('products.stripe.webhookDescription')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Additional Settings */}
            <div className="space-y-4">
              <Label className="text-base">{t('products.stripe.accountSettingsSection')}</Label>
              
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="stripe_account_country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('products.stripe.countryLabel')}</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="US"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="stripe_account_currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('products.stripe.currencyLabel')}</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="USD"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-4">
              <Button
                type="button"
                onClick={validateCredentials}
                variant="outline"
                disabled={isValidating || !form.watch('stripe_publishable_key') || !form.watch('stripe_secret_key')}
              >
                {isValidating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t('products.stripe.validateKeys')}
              </Button>

              <Button
                type="submit"
                disabled={isSaving || !form.formState.isValid}
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <Save className="h-4 w-4 mr-2" />
                {t('products.stripe.saveConfiguration')}
              </Button>

              {config?.configured && (
                <Button
                  type="button"
                  onClick={handleRemove}
                  variant="destructive"
                  className="ml-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('products.stripe.removeConfiguration')}
                </Button>
              )}
            </div>
          </form>
        </Form>

        {/* Help Section */}
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <strong>{t('products.stripe.helpHeading')}</strong>
              <ol className="list-decimal pl-4 space-y-1 text-sm">
                <li>
                  {t('products.stripe.helpSteps.0')}{' '}
                  <a
                    href="https://dashboard.stripe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {t('products.stripe.dashboardLink')}
                  </a>
                </li>
                <li>{t('products.stripe.helpSteps.1')}</li>
                <li>{t('products.stripe.helpSteps.2')}</li>
                <li>{t('products.stripe.helpSteps.3')}</li>
              </ol>
              <p className="text-sm mt-2">
                <strong>{t('products.stripe.individualAccountsHeading')}</strong>{' '}
                {t('products.stripe.individualAccountsNote')}
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
